import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, GraduationCap, School } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LessonRow } from "@/pages/enseignement/AgendaTeachingPage";
import { RESOURCE_ICONS, isMediaResource, openTeachingResource, ResourcePlayerDialog } from "@/pages/enseignement/teachUtils";

// Fiche pédagogique privée d'un élève (enseignant : édition ; élève : lecture seule de la sienne)
export default function EleveFichePage() {
  const { relId } = useParams();
  const { tspace, lang, t } = useAuth();
  const isTeacher = tspace?.my_role === "teacher";
  const navigate = useNavigate();
  const [rel, setRel] = useState(null);
  const [form, setForm] = useState({ instrument: "", level: "", notes: "" });
  const [lessons, setLessons] = useState([]);
  const [resources, setResources] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [sharePick, setSharePick] = useState("");
  const [player, setPlayer] = useState(null);
  const openRes = (r) => (isMediaResource(r) ? setPlayer(r) : openTeachingResource(r));
  const [notFound, setNotFound] = useState(false);

  const load = () => api.get(`/teaching/students/${relId}`).then((r) => {
    setRel(r.data);
    setForm({ instrument: r.data.instrument || "", level: r.data.level || "", notes: r.data.notes || "" });
  }).catch(() => setNotFound(true));
  useEffect(() => {
    load();
    // Historique des cours individuels de l'élève (visible par l'enseignant et l'élève concerné)
    api.get("/teaching/lessons").then((r) => setLessons(r.data.lessons.filter((l) => l.student_relation_id === relId))).catch(() => {});
    // Ressources partagées avec cet élève
    api.get("/teaching/resources").then((r) => setResources(r.data.resources)).catch(() => {});
    // Devoirs ciblant cet élève (individuels ; les devoirs de classe restent sur la page classe)
    api.get("/teaching/assignments").then((r) => setAssignments(r.data.assignments.filter((a) => a.student_relation_id === relId))).catch(() => {});
  }, [relId]);

  // Partage rapide depuis la fiche (§54) : ajoute la relation aux partages de la ressource
  const quickShare = async () => {
    const res = resources.find((r) => r.id === sharePick);
    if (!res) return;
    try {
      await api.put(`/teaching/resources/${res.id}/shares`, {
        student_relation_ids: [...new Set([...(res.student_relation_ids || []), relId])],
        class_ids: res.class_ids || [],
      });
      toast.success(t("teach.saved"));
      setSharePick("");
      api.get("/teaching/resources").then((r) => setResources(r.data.resources)).catch(() => {});
    } catch (e) { toast.error(e.friendly); }
  };

  const save = async () => {
    try {
      const r = await api.put(`/teaching/students/${relId}`, form);
      setRel(r.data);
      toast.success(t("teach.saved"));
    } catch (e) { toast.error(e.friendly); }
  };

  const toggleStatus = async () => {
    try {
      const r = await api.put(`/teaching/students/${relId}`, { status: rel.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" });
      setRel(r.data);
      toast.success(t("teach.saved"));
    } catch (e) { toast.error(e.friendly); }
  };

  if (notFound) return <p className="text-sm t-secondary py-10 text-center" data-testid="student-not-found">404</p>;
  if (!rel) return <p className="t-secondary text-sm" data-testid="student-loading">{t("common.loading")}</p>;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="student-sheet">
      <button onClick={() => navigate("/enseignement/eleves")} className="text-sm t-secondary hover:text-white flex items-center gap-1" data-testid="student-back">
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </button>

      <div className="app-card p-6 space-y-1">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2" data-testid="student-name">
          <GraduationCap className="h-5 w-5 brand" /> {rel.prenom} {rel.nom}
        </h2>
        <p className="text-xs t-secondary font-mono">{rel.login_id}</p>
        <p className="text-xs t-secondary">
          {rel.account_status === "PENDING" ? t("teach.pending_account") : t("teach.account_active")}
          {rel.status === "INACTIVE" && ` — ${t("teach.inactive_relation")}`}
        </p>
        {rel.classes.length > 0 && (
          <div className="flex gap-1.5 flex-wrap pt-2">
            {rel.classes.map((c) => (
              <span key={c.id} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-400 border border-sky-500/30 flex items-center gap-1" data-testid={`student-class-chip-${c.id}`}>
                <School className="h-3 w-3" /> {c.name}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="app-card p-6 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("param.instrument_one")}</Label>
            {isTeacher ? (
              <Input value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fiche-instrument-input" />
            ) : (
              <p className="text-sm mt-1" data-testid="fiche-instrument">{rel.instrument || "—"}</p>
            )}
          </div>
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.level")}</Label>
            {isTeacher ? (
              <Input value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fiche-level-input" />
            ) : (
              <p className="text-sm mt-1" data-testid="fiche-level">{rel.level || "—"}</p>
            )}
          </div>
        </div>
        <div>
          <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.notes")}</Label>
          {isTeacher ? (
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} className="mt-1 bg-transparent border-app" data-testid="fiche-notes-input" />
          ) : (
            <p className="text-sm mt-1 whitespace-pre-wrap" data-testid="fiche-notes">{rel.notes || "—"}</p>
          )}
        </div>
        {isTeacher && (
          <div className="flex gap-2 flex-wrap pt-1">
            <Button className="brand-bg text-white" onClick={save} data-testid="fiche-save-button">{t("common.save")}</Button>
            <Button variant="outline" className="border-app text-rose-400" onClick={toggleStatus} data-testid="fiche-toggle-status">
              {rel.status === "ACTIVE" ? t("teach.deactivate_relation") : t("teach.reactivate_relation")}
            </Button>
          </div>
        )}
      </div>

      <div className="app-card p-6 space-y-2" data-testid="fiche-lessons">
        <h3 className="font-heading font-bold">{t("teach.lessons_history")}</h3>
        {lessons.length === 0 ? (
          <p className="text-sm t-secondary" data-testid="fiche-lessons-empty">{t("teach.no_lessons")}</p>
        ) : (
          lessons.map((l) => <LessonRow key={l.id} l={l} lang={lang} t={t} canEdit={false} />)
        )}
      </div>

      <div className="app-card p-6 space-y-2" data-testid="fiche-assignments">
        <h3 className="font-heading font-bold">{t("teach.nav_assignments")}</h3>
        {assignments.length === 0 ? (
          <p className="text-sm t-secondary" data-testid="fiche-assignments-empty">{t("teach.no_assignments")}</p>
        ) : (
          assignments.map((a) => (
            <div key={a.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03]" data-testid={`fiche-assignment-${a.id}`}>
              <span className="text-sm font-medium truncate flex-1">{a.title}</span>
              <span className="text-[10px] t-secondary shrink-0">{a.due_date || t("teach.no_due")}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${
                (isTeacher ? a.done_count === a.total_count && a.total_count > 0 : a.my_done)
                  ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                  : "bg-amber-500/15 text-amber-400 border-amber-500/30"
              }`}>
                {t(isTeacher ? "teach.done_count" : (a.my_done ? "teach.done_badge" : "teach.todo_badge"), isTeacher ? { done: a.done_count, total: a.total_count } : {})}
              </span>
            </div>
          ))
        )}
      </div>

      {(() => {
        const shared = isTeacher ? resources.filter((r) => (r.student_relation_ids || []).includes(relId)) : resources;
        const shareable = resources.filter((r) => !(r.student_relation_ids || []).includes(relId));
        return (
          <div className="app-card p-6 space-y-2" data-testid="fiche-resources">
            <h3 className="font-heading font-bold">{t("teach.shared_resources")}</h3>
            {shared.length === 0 ? (
              <p className="text-sm t-secondary" data-testid="fiche-resources-empty">{t("teach.no_resources")}</p>
            ) : (
              shared.map((r) => {
                const Icon = RESOURCE_ICONS[r.type] || RESOURCE_ICONS.document;
                return (
                  <button key={r.id} onClick={() => openRes(r)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 text-left" data-testid={`fiche-resource-${r.id}`}>
                    <Icon className="h-4 w-4 brand shrink-0" />
                    <span className="text-sm font-medium truncate">{r.title}</span>
                    <span className="text-[10px] t-secondary ml-auto shrink-0">{t(`teach.type_${r.type}`)}</span>
                  </button>
                );
              })
            )}
            {isTeacher && shareable.length > 0 && (
              <div className="flex gap-2 pt-2 border-t border-app">
                <Select value={sharePick} onValueChange={setSharePick}>
                  <SelectTrigger className="bg-transparent border-app" data-testid="fiche-share-select"><SelectValue placeholder={t("teach.quick_share")} /></SelectTrigger>
                  <SelectContent>
                    {shareable.map((r) => <SelectItem key={r.id} value={r.id}>{r.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button className="brand-bg text-white shrink-0" onClick={quickShare} disabled={!sharePick} data-testid="fiche-share-confirm">{t("teach.share")}</Button>
              </div>
            )}
          </div>
        );
      })()}
      {player && <ResourcePlayerDialog resource={player} onClose={() => setPlayer(null)} />}
    </div>
  );
}
